// BC-95 — derive a small set of dominant "brand" colours from an uploaded image
// so admins get one-click colour suggestions instead of hand-picking hex codes.
// Dependency-free: downsamples onto a canvas and buckets pixels by coarse hue.
// Skips near-white / near-black / transparent pixels so a suggestion is an
// actual brand colour, not the background.

const SAMPLE_SIZE = 48 // downsample target (px); keeps the scan cheap
const BUCKET = 24 // colour-quantisation step per channel

function toHex(r: number, g: number, b: number): string {
  const h = (n: number) => n.toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}

function isBackgroundish(r: number, g: number, b: number, a: number): boolean {
  if (a < 200) return true // transparent
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max > 240 && min > 240) return true // near-white
  if (max < 24) return true // near-black
  return false
}

/**
 * Returns up to `count` dominant hex colours, most frequent first.
 * Resolves to [] on any failure (never throws to the caller).
 */
export function extractPalette(file: File, count = 5): Promise<string[]> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const img = new Image()

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const scale = Math.min(1, SAMPLE_SIZE / Math.max(img.width, img.height))
        canvas.width = Math.max(1, Math.round(img.width * scale))
        canvas.height = Math.max(1, Math.round(img.height * scale))
        const ctx = canvas.getContext('2d')
        if (!ctx) return resolve([])

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)

        const buckets = new Map<string, { count: number; r: number; g: number; b: number }>()
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]
          const a = data[i + 3]
          if (isBackgroundish(r, g, b, a)) continue

          const key = `${Math.round(r / BUCKET)}-${Math.round(g / BUCKET)}-${Math.round(b / BUCKET)}`
          const entry = buckets.get(key)
          if (entry) {
            entry.count++
            entry.r += r
            entry.g += g
            entry.b += b
          } else {
            buckets.set(key, { count: 1, r, g, b })
          }
        }

        const swatches = [...buckets.values()]
          .sort((a, b) => b.count - a.count)
          .slice(0, count)
          .map((e) => toHex(Math.round(e.r / e.count), Math.round(e.g / e.count), Math.round(e.b / e.count)))

        resolve(swatches)
      } catch {
        resolve([])
      } finally {
        URL.revokeObjectURL(url)
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve([])
    }

    img.src = url
  })
}
